<template>
  <div class="sticky top-0 z-50 font-[IBM]">
    <ComplaintBanner />
    <div class="bg-white">
      <!-- Header para desktop -->
      <div class="hidden lg:flex py-3 items-center">
        <div class="flex-none pl-10">
          <MainLogo class="flex" />
        </div>
        <div class="grow">
          <div class="flex justify-center items-center h-full">
            <!-- Planos menu item (primeiro) -->
            <div class="font-['Poppins'] font-semibold mx-2">
              <router-link 
                to="/planos" 
                :class="isActivePlanos ? 'text-[#5095FD]' : 'text-[#0B2651] hover:text-[#5095FD]'"
                class="transition-colors"
              >
                Planos
              </router-link>
            </div>

            <!-- Solutions dropdown menu (segundo) -->
            <div class="font-['Poppins'] font-bold mx-2 relative group">
              <div class="flex items-center cursor-pointer" :class="isActiveSolution ? 'text-[#5095FD]' : 'text-[#0B2651] hover:text-[#5095FD]'">
                <span class="mr-1">Soluções</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9L12 15L18 9" :stroke="isActiveSolution ? '#5095FD' : '#0B2651'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              
              <!-- Submenu dropdown -->
              <div class="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md py-3 px-4 z-10 hidden group-hover:block submenu" style="box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);">
                <div class="flex flex-col gap-2">
                  <div v-for="(solution, index) in solutions" :key="index" 
                       class="whitespace-nowrap font-['Poppins'] font-bold text-[16px] text-[#0C3865] hover:text-[#5095FD] transition-colors">
                    <router-link v-if="solution.isRouter" :to="solution.link">
                      {{ solution.label }}
                    </router-link>
                    <a v-else :href="solution.link" target="_blank">
                      {{ solution.label }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Regular menu items (restantes) -->
            <div
              class="font-['Poppins'] font-semibold mx-2"
              v-for="item of menuItems"
              :key="item"
            >
              <router-link 
                v-if="item.isRouter" 
                :to="item.link"
                :class="getMenuItemClass(item.link)"
                class="transition-colors"
              >
                {{ item.label }}
              </router-link>
              <a 
                v-else 
                :href="item.link" 
                target="_blank"
                class="text-[#0B2651] hover:text-[#5095FD] transition-colors"
              >
                {{ item.label }}
              </a>
            </div>
          </div>
        </div>
        <div class="flex-none">
          <div class="flex">
            <!-- Talk to specialist button -->
            <div class="mx-2" v-if="!hideWhatsAppButton">
              <WhatsAppButton variant="header" />
            </div>
          </div>
        </div>
      </div>

      <!-- Novo header para mobile -->
      <div class="lg:hidden flex items-center px-6 py-4 relative">
        <div @click="toggle()" class="cursor-pointer absolute left-6">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 8H21" stroke="#343434" stroke-width="2" stroke-linecap="round"/>
            <path d="M3 16H21" stroke="#343434" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="flex-none mx-auto">
          <MainLogo class="flex" />
        </div>
      </div>
      <!-- Linha divisória para mobile -->
      <hr class="lg:hidden h-[1px] border-solid border-0 border-t border-black opacity-10 mt-0" />
    </div>

    <hr class="hidden lg:block h-[0px] border-solid border-1 border-gray-300 opacity-50" />

    <div
      :class="{
        'flex flex-col top-[90px] z-50 absolute w-full bg-white py-10': status,
        hidden: !status,
      }"
    >
      <div class="mt-3 mx-4">
        <!-- Mobile Planos item (primeiro) -->
        <div class="mb-2 font-['Poppins'] font-semibold">
          <router-link 
            to="/planos" 
            @click="toggle()"
            :class="isActivePlanos ? 'text-[#5095FD]' : 'text-[#0B2651] hover:text-[#5095FD]'"
            class="transition-colors"
          >
            Planos
          </router-link>
        </div>

        <!-- Mobile menu dropdown (segundo) -->
        <div class="font-['Poppins'] font-bold flex items-center mb-2 justify-center" :class="isActiveSolution ? 'text-[#5095FD]' : 'text-[#0B2651] hover:text-[#5095FD]'">
          <span class="mr-1 cursor-pointer" @click="toggleSolutionsMobile">Soluções</span>
          <svg @click="toggleSolutionsMobile" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="cursor-pointer">
            <path d="M6 9L12 15L18 9" :stroke="isActiveSolution ? '#5095FD' : '#0B2651'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        
        <!-- Mobile solutions submenu -->
        <div v-if="showSolutionsMobile" class="ml-4 mb-4">
          <div v-for="(solution, index) in solutions" :key="index" 
               class="mb-2 font-['Poppins'] font-semibold text-[#0C3865]">
            <router-link v-if="solution.isRouter" :to="solution.link" @click="toggle()">
              {{ solution.label }}
            </router-link>
            <a v-else :href="solution.link" target="_blank" @click="toggle()">
              {{ solution.label }}
            </a>
          </div>
        </div>
        
        <!-- Mobile menu items (restantes) -->
        <div v-for="item of menuItems" :key="item" class="mb-2 font-['Poppins'] font-semibold">
          <router-link 
            v-if="item.isRouter" 
            :to="item.link" 
            @click="toggle()"
            :class="getMenuItemClass(item.link)"
            class="transition-colors"
          >
            {{ item.label }}
          </router-link>
          <a 
            v-else 
            :href="item.link" 
            target="blank"
            class="text-[#0B2651] hover:text-[#5095FD] transition-colors"
          >
            {{ item.label }}
          </a>
        </div>
        
        <!-- Mobile talk to specialist button -->
        <div class="mt-4 mb-2">
          <WhatsAppButton variant="header" v-if="!hideWhatsAppButton" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainLogo from "@/components/MainLogo.vue";
import WhatsAppButton from "@/components/WhatsAppButton.vue";
import ComplaintBanner from "@/components/ComplaintBanner.vue";

export default {
  components: {
    MainLogo,
    WhatsAppButton,
    ComplaintBanner,
  },
  computed: {
    hideWhatsAppButton() {
      // Esconde o botão WhatsApp na página de empresas e empresas/id
      return this.$route.path === '/empresas' ||
             this.$route.name === 'empresas-id' || 
             this.$route.path.startsWith('/empresas/');
    },
    isActiveSolution() {
      // Verifica se a rota atual é uma das soluções
      const solutionPaths = this.solutions.map(solution => solution.link);
      return solutionPaths.includes(this.$route.path);
    },
    isActivePlanos() {
      return this.$route.path === '/planos';
    },
    isActiveQuemSomos() {
      return this.$route.path === '/quem-somos';
    },
    isActiveEmpresas() {
      return this.$route.path === '/empresas';
    },
    getMenuItemClass() {
      return (path) => {
        return this.$route.path === path ? 'text-[#5095FD]' : 'text-[#0B2651] hover:text-[#5095FD]';
      };
    }
  },
  data() {
    return {
      status: false,
      showSolutions: false,
      showSolutionsMobile: false,
      clientPanel: this.ClientPanel,
      solutions: [
        { label: "Canal de Denúncias", link: "/canal-de-denuncia", isRouter: true },
        { label: "Canal de Denúncias CIPA", link: "/canal-de-denuncia-cipa", isRouter: true },
        { label: "Canal de Ouvidoria", link: "/canal-de-ouvidoria", isRouter: true },
        { label: "Canal LGPD", link: "/canal-lgpd", isRouter: true },
      ],
      menu: [
        { label: "Planos", link: "/planos", isRouter: true },
        { label: "Quem Somos", link: "/quem-somos", isRouter: true },
        { label: "Clientes", link: "/empresas", isRouter: true },
        { label: "Blog", link: "https://blog.bcompliance.com.br/", isRouter: false },
      ],
      // Menu items sem "Planos" (será exibido separadamente)
      menuItems: [
        { label: "Quem Somos", link: "/quem-somos", isRouter: true },
        { label: "Clientes", link: "/empresas", isRouter: true },
        { label: "Blog", link: "https://blog.bcompliance.com.br/", isRouter: false },
      ],
    };
  },
  methods: {
    toggle() {
      this.status = !this.status;
      // Fechar o submenu quando fechar o menu principal na versão mobile
      if (!this.status) {
        this.showSolutionsMobile = false;
      }
    },
    toggleSolutionsMobile() {
      this.showSolutionsMobile = !this.showSolutionsMobile;
    },
    goTo(link) {
      this.$router.push({ path: link });
      // window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
};
</script>

<style>
.line {
  height: 0px;
  border: none;
  border-top: 1px solid black;
}

/* Adicionar área de segurança para navegação do submenu */
.submenu::before {
  content: '';
  position: absolute;
  top: -20px;  /* Área invisível acima do submenu */
  left: 0;
  width: 100%;
  height: 20px;
}

.group:hover .submenu {
  opacity: 1;
  visibility: visible;
  transition-delay: 0s;
}

.submenu {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0.5s ease;
  transition-delay: 0.3s;
}

/* Animação para o submenu mobile */
.ml-4.mb-4 {
  opacity: 1;
  transition: opacity 0.5s ease;
  transform-origin: top;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
